import { canOpenResource } from './access';
import {
  configurationSummary,
  dataCatalog,
  permissionPreview,
  recordingReferences,
} from './administration';
import {
  type ActionPreview,
  type ActionRecommendation,
  contextKey,
  type DomainCommandContext,
  DomainFault,
  type DomainReadContext,
  type JsonObject,
  type QueryInput,
  type SearchHit,
} from './contracts';
import { administrationDomain } from './domains/administration';
import {
  analysisDomain,
  analysisQualityDomain,
  analysisTemplateDomain,
} from './domains/analysis';
import {
  isExecutionProjection,
  publishedExecutionSettings,
} from './domains/analysis/configuration';
import {
  collectionData,
  hasCollectionWork,
  reduceCollection,
} from './domains/collection/service';
import { deviceCommand } from './domains/device';
import {
  knowledgeDomain,
  learningAccessOverride,
  learningDomain,
} from './domains/learning';
import { locationDomain } from './domains/location';
import { operationsDomain, pilotDomain } from './domains/operations';
import { recordingDomain, validateEvidence } from './domains/recording';
import { reviewDomain } from './domains/review';
import { guardStudentLegacyCommand, studentDomain } from './domains/student';
import { supportDomain } from './domains/support';
import { expandTerms, workDomain } from './domains/work';
import { insidePolygon, moduleCommand, policyAllows } from './modules';
import type { Repository } from './persistence';
import {
  getDomain,
  migrateDomains,
  registerDomain,
  registeredDomains,
  resolveDomainCommand,
} from './registry';
import { createSeed } from './seed';
import type {
  Context,
  Entity,
  Evidence,
  Kind,
  SessionIdentity,
  Snapshot,
} from './types';
import { canAccessEntity } from './visibility';
import {
  recommendation,
  workspaceResources,
  workspaceVisible,
} from './workspace';

registerDomain(workDomain);
registerDomain(learningDomain);
registerDomain(knowledgeDomain);
registerDomain(analysisDomain);
registerDomain(analysisTemplateDomain);
registerDomain(analysisQualityDomain);
registerDomain(recordingDomain);
registerDomain(reviewDomain);
registerDomain(locationDomain);
registerDomain(operationsDomain);
registerDomain(pilotDomain);
registerDomain(studentDomain);
registerDomain(supportDomain);
registerDomain(administrationDomain);
export class BusinessError extends DomainFault {
  constructor(
    public code: string,
    message: string,
  ) {
    super(code, message);
  }
}
const contentKinds: Kind[] = [
  'recording',
  'clue',
  'job',
  'case',
  'lesson',
  'plan',
  'feedback',
  'knowledge',
  'followUp',
  'student',
  'correction',
];
const contentRoles = [
  'campusManager',
  'groupOperator',
  'regionManager',
  'teacher',
  'reviewer',
  'researchLead',
  'studentService',
];
export function canRead(item: Entity, context: Context): boolean {
  const learningAccess = learningAccessOverride(item, context);
  if (learningAccess !== undefined) return learningAccess;
  if (
    context.role === 'teacher' &&
    ['device', 'binding', 'teacher'].includes(item.kind) &&
    item.memberUuid !== context.viewMemberUuid
  )
    return false;
  if (
    context.role === 'studentService' &&
    ['student', 'followUp', 'feedback'].includes(item.kind) &&
    item.memberUuid !== context.viewMemberUuid &&
    item.owner !== context.viewMemberUuid
  )
    return false;

  if (item.fields?.softDeleted === 'true') return false;
  if (
    item.kind === 'knowledge' &&
    item.status !== 'published' &&
    ![
      'campusManager',
      'groupOperator',
      'regionManager',
      'researchLead',
    ].includes(context.role) &&
    item.fields?.authorView !== context.viewMemberUuid
  )
    return false;
  if (
    context.role === 'reviewer' &&
    ['clue', 'case', 'task'].includes(item.kind) &&
    item.owner &&
    item.owner !== context.viewMemberUuid
  )
    return false;
  if (context.campus !== '全部校区' && item.campus !== context.campus)
    return false;
  if (contentKinds.includes(item.kind) && !contentRoles.includes(context.role))
    return item.kind === 'knowledge' && item.status === 'published';
  if (
    context.role === 'teacher' &&
    contentKinds.includes(item.kind) &&
    item.kind !== 'knowledge'
  )
    return item.memberUuid === context.viewMemberUuid;
  if (
    context.role === 'deviceManager' &&
    ![
      'device',
      'binding',
      'batch',
      'model',
      'station',
      'position',
      'zone',
      'repair',
      'inventory',
      'ticket',
      'help',
      'message',
      'task',
      'audit',
    ].includes(item.kind)
  )
    return false;
  if (context.role === 'support')
    return [
      'device',
      'repair',
      'ticket',
      'help',
      'message',
      'task',
      'audit',
    ].includes(item.kind);
  return true;
}
const permissions: Record<string, string[]> = {
  workspaceTask: [
    'campusManager',
    'regionManager',
    'groupOperator',
    'teacher',
    'reviewer',
    'deviceManager',
    'studentService',
    'researchLead',
  ],
  bind: ['deviceManager', 'campusManager', 'groupOperator'],
  return: ['deviceManager', 'campusManager', 'groupOperator'],
  collect: ['deviceManager', 'campusManager', 'groupOperator'],
  queue: ['deviceManager', 'campusManager', 'groupOperator'],
  exportMetadata: ['campusManager', 'groupOperator', 'regionManager'],
  dataPolicy: ['systemAdmin', 'groupOperator'],
  replaceDevice: ['deviceManager', 'campusManager', 'groupOperator'],
  locationStep: ['deviceManager', 'campusManager', 'groupOperator'],
  locationConfirm: [
    'deviceManager',
    'campusManager',
    'groupOperator',
    'regionManager',
  ],
  importAudio: ['teacher', 'deviceManager', 'campusManager', 'groupOperator'],
  confirmLink: ['teacher', 'campusManager', 'groupOperator', 'researchLead'],
  deviceReport: ['deviceManager', 'campusManager', 'groupOperator'],
  read: Object.keys({
    groupLeader: 1,
    groupOperator: 1,
    regionManager: 1,
    campusManager: 1,
    researchLead: 1,
    studentService: 1,
    teacher: 1,
    reviewer: 1,
    deviceManager: 1,
    systemAdmin: 1,
    support: 1,
  }),
  jobControl: [
    'teacher',
    'campusManager',
    'reviewer',
    'researchLead',
    'groupOperator',
  ],
  analyze: [
    'teacher',
    'campusManager',
    'reviewer',
    'researchLead',
    'groupOperator',
  ],
  revise: ['teacher', 'campusManager', 'reviewer'],
  mark: ['teacher', 'campusManager', 'reviewer', 'researchLead'],
  clue: ['teacher', 'campusManager', 'reviewer', 'groupOperator'],
  assign: ['campusManager', 'reviewer', 'regionManager', 'groupOperator'],
  review: ['reviewer', 'campusManager', 'groupOperator'],
  case: ['reviewer', 'campusManager', 'groupOperator'],
  submit: ['campusManager', 'reviewer', 'groupOperator'],
  close: ['campusManager', 'regionManager', 'groupOperator'],
  reopen: ['campusManager', 'regionManager', 'groupOperator'],
  task: [
    'campusManager',
    'reviewer',
    'teacher',
    'deviceManager',
    'studentService',
    'researchLead',
    'support',
    'groupOperator',
  ],
};
export function allowed(action: string, context: Context) {
  if (action === 'recording.associate')
    return permissions.confirmLink.includes(context.role);
  if (action.startsWith('recording.'))
    return [
      'teacher',
      'campusManager',
      'groupOperator',
      'regionManager',
      'researchLead',
      'reviewer',
      'studentService',
    ].includes(context.role);
  return (
    permissions[action]?.includes(context.role) ??
    !['groupLeader', 'teacher', 'deviceManager', 'support'].includes(
      context.role,
    )
  );
}
export interface Command {
  audioBlob?: Blob;
  action: string;
  uuid?: string;
  title?: string;
  memberUuid?: string;
  note?: string;
  dueAt?: string;
  conclusion?: string;
  evidence?: Evidence;
  startAt?: string;
  meta?: { commandUuid: string; expectedRevision?: number };
  kind?: Kind;
  fields?: Record<string, string>;
}
const stamp = (s: Snapshot) => s.now;
function append(
  s: Snapshot,
  kind: Kind,
  title: string,
  extra: Partial<Entity> = {},
): Entity {
  const row: Entity = {
    uuid: crypto.randomUUID(),
    kind,
    title,
    campus: s.context.campus === '全部校区' ? '第一校区' : s.context.campus,
    memberUuid: s.context.viewMemberUuid,
    status: 'draft',
    createdAt: stamp(s),
    updatedAt: stamp(s),
    revision: 1,
    note: '',
    ...extra,
  };
  s.entities.push(row);
  return row;
}
function item(s: Snapshot, uuid: string | undefined, kind?: Kind): Entity {
  const row = s.entities.find(
    (e) => e.uuid === uuid && (!kind || e.kind === kind),
  );
  if (!row || !canRead(row, s.context) || !policyAllows(s, row.kind, 'read'))
    throw new BusinessError(
      'FORBIDDEN',
      '对象不存在或当前业务视角没有访问权限。',
    );
  return row;
}
function requireState(row: Entity, states: string[]) {
  if (!states.includes(row.status))
    throw new BusinessError(
      'INVALID_TRANSITION',
      '对象状态已变化，请刷新后重试。',
    );
}
function required(value: string | undefined, name: string) {
  if (!value?.trim()) throw new BusinessError('VALIDATION', `请填写${name}。`);
  return value.trim();
}
function changed(row: Entity, s: Snapshot) {
  row.revision++;
  row.updatedAt = stamp(s);
}
function notify(s: Snapshot, row: Entity, title: string) {
  const category =
    row.kind === 'task'
      ? 'action'
      : ['batch', 'repair'].includes(row.kind)
        ? 'alert'
        : 'update';
  const preferences =
    s.domainData?.work?.[`subscriptions:${contextKey(s.context)}`];
  if (
    preferences &&
    typeof preferences === 'object' &&
    !Array.isArray(preferences) &&
    preferences[category] === false
  )
    return;

  append(s, 'message', title, {
    status: 'unread',
    campus: row.campus,
    memberUuid: row.memberUuid,
    sourceUuid: row.uuid,
  });
}
function makeTask(s: Snapshot, row: Entity, title: string) {
  append(s, 'task', title, {
    status: 'pending',
    sourceUuid: row.uuid,
    campus: row.campus,
    memberUuid: row.memberUuid,
    owner: row.owner,
    dueAt: row.dueAt,
  });
  notify(s, row, title);
}
function checkEvidence(s: Snapshot, evidence: Evidence | undefined): Evidence {
  if (!evidence) throw new BusinessError('VALIDATION', '请选择录音片段。');
  const recording = item(s, evidence.recordingUuid, 'recording');
  return validateEvidence(recording, evidence);
}
const auditValues = (row: Entity | undefined): Record<string, string> =>
  row
    ? Object.fromEntries([
        ...['title', 'status', 'campus', 'owner', 'memberUuid'].map((key) => [
          key,
          String(row[key as keyof Entity] || ''),
        ]),
        ...[
          'parallel',
          'timeoutMs',
          'keyword',
          'exclusion',
          'analysisLimit',
          'unitPrice',
          'expiresAt',
          'release',
          'read',
          'write',
          'role',
          'kind',
          'strategy',
          'parentUuid',
          'softDeleted',
          'retentionLock',
        ].map((key) => [`fields.${key}`, row.fields?.[key] || '']),
      ])
    : {};
const auditDifference = (
  before: Record<string, string>,
  after: Entity | undefined,
) => {
  const current = auditValues(after);
  return JSON.stringify(
    [...new Set([...Object.keys(before), ...Object.keys(current)])]
      .filter((key) => before[key] !== current[key])
      .map((field) => ({
        field,
        before: before[field] || '',
        after: current[field] || '',
      })),
  );
};
export function reduceCommand(
  s: Snapshot,
  input: Command,
  identity: SessionIdentity,
): { state: Snapshot; uuid: string } {
  if (!identity.memberUuid || !identity.tenantUuid)
    throw new BusinessError('UNAUTHENTICATED', '请先通过真实 SSO 登录。');
  const beforeAudit = auditValues(
    s.entities.find(
      (row) => row.uuid === (input.uuid || input.fields?.targetUuid),
    ),
  );
  const commandUuid = input.meta?.commandUuid || crypto.randomUUID();
  if (s.receipts[commandUuid])
    return { state: s, uuid: s.receipts[commandUuid] };
  guardStudentLegacyCommand(
    s.entities.find((e) => e.uuid === input.uuid),
    input.action,
  );
  if (input.action.includes('.')) {
    const resolved = resolveDomainCommand(input.action);
    if (!resolved) throw new BusinessError('UNAVAILABLE', '领域命令尚未注册');
    const { module, command } = resolved;
    if (
      !canOpenResource(module.resource, s) ||
      !command.roles.includes(s.context.role)
    )
      throw new BusinessError('FORBIDDEN', '当前业务视角无权执行此操作');
    if (command.kind && !policyAllows(s, command.kind, 'write'))
      throw new BusinessError('FORBIDDEN', '业务权限禁止此操作');
    const secondaryKind =
      input.action === 'knowledge.reuse'
        ? input.fields?.kind === 'plan'
          ? 'plan'
          : 'teacher'
        : input.action === 'learning.linkRecording'
          ? 'lesson'
          : undefined;
    if (secondaryKind && !policyAllows(s, secondaryKind, 'write'))
      throw new BusinessError('FORBIDDEN', '业务权限禁止修改引用目标或课次');
    if (input.uuid) {
      const row = s.entities.find((e) => e.uuid === input.uuid);
      if (!row || !canAccessEntity(s, row, canRead))
        throw new BusinessError('FORBIDDEN', '对象不可访问');
      if (
        input.meta?.expectedRevision === undefined ||
        row.revision !== input.meta.expectedRevision
      )
        throw new BusinessError('CONFLICT', '对象版本已变化，请重新预览');
    }
    const result = command.execute(
      domainContext(s, identity, module.namespace, commandUuid),
      input,
    );
    const uuid = result?.uuid || commandUuid;
    append(s, 'audit', command.label || '业务记录已更新', {
      sourceUuid: result?.uuid,
      status: 'completed',
      note: input.note || '',
      fields: {
        actorMemberUuid: identity.memberUuid,
        commandUuid,
        namespace: module.namespace,
        role: s.context.role,
        viewMemberUuid: s.context.viewMemberUuid,
        realOccurredAt: new Date().toISOString(),
        fieldChanges: auditDifference(beforeAudit, result),
      },
    });
    s.receipts[commandUuid] = uuid;
    s.revision++;
    return { state: s, uuid };
  }
  if (
    !input.action.startsWith('module') &&
    !['inventory', 'knowledgeReuse', 'device'].includes(input.action) &&
    !allowed(input.action, s.context)
  )
    throw new BusinessError(
      'FORBIDDEN',
      '当前业务视角无权执行此操作，请在演示工具中切换合适角色。',
    );
  const row = input.uuid ? item(s, input.uuid) : undefined;
  if (
    ['locationStep', 'locationConfirm'].includes(input.action) ||
    (input.action.startsWith('module') &&
      ['zone', 'pilot'].includes(row?.kind || input.kind || ''))
  ) {
    throw new BusinessError(
      'INVALID_TRANSITION',
      '请在位置或试点专用工作区执行版本化命令，旧入口不能绕过样本预览和人工评审。',
    );
  }
  if (
    input.action !== 'read' &&
    (row?.fields?.learningManaged === 'true' ||
      Object.keys(input.fields || {}).some((key) =>
        [
          'learningManaged',
          'learningPlan',
          'learningObservations',
          'learningSections',
          'learningProfile',
        ].includes(key),
      ))
  )
    throw new BusinessError(
      'FORBIDDEN',
      '教学与知识资料须使用专用流程，不能绕过确认与审核',
    );
  const writeKind =
    input.action === 'workspaceTask'
      ? 'task'
      : input.action === 'case'
        ? 'case'
        : input.action === 'clue'
          ? 'clue'
          : input.action === 'analyze'
            ? 'job'
            : row?.kind || input.kind;
  if (
    writeKind &&
    input.action !== 'read' &&
    !policyAllows(s, writeKind, 'write')
  )
    throw new BusinessError('FORBIDDEN', '模拟业务权限策略禁止此资源写入');
  if (
    row &&
    input.meta?.expectedRevision !== undefined &&
    row.revision !== input.meta.expectedRevision
  )
    throw new BusinessError(
      'CONFLICT',
      '对象已被其他操作更新，请重新加载后提交。',
    );
  if (
    input.fields?.previewAt &&
    (!Number.isFinite(Number(input.fields.previewAt)) ||
      Math.abs(Date.now() - Number(input.fields.previewAt)) > 900000)
  )
    throw new BusinessError('CONFLICT', '预览已过期，请重新预览。');
  let result = row;
  switch (input.action) {
    case 'device': {
      result = deviceCommand(s, input, { append, get: item, touch: changed });
      break;
    }
    case 'workspaceTask': {
      if (input.meta?.expectedRevision === undefined)
        throw new BusinessError('VALIDATION', '请先预览来源版本后确认。');
      const advice = row && recommendation(row, s.now);
      if (!row || !advice)
        throw new BusinessError(
          'CONFLICT',
          '建议已不再适用，请重新查看工作台。',
        );
      const resource = workspaceResources[row.kind];
      if (
        !resource ||
        !canOpenResource(resource, s) ||
        !workspaceVisible(
          {
            state: s,
            get: (uuid) => {
              try {
                return item(s, uuid);
              } catch {
                return undefined;
              }
            },
          },
          row,
        ) ||
        !policyAllows(s, 'task', 'write')
      )
        throw new BusinessError('FORBIDDEN', '无权为此来源安排任务。');
      const previewAt = Number(input.fields?.previewAt);
      if (
        !Number.isFinite(previewAt) ||
        Math.abs(Date.now() - previewAt) > 15 * 60 * 1000
      )
        throw new BusinessError('CONFLICT', '行动预览已过期，请重新预览。');
      const owner = required(input.memberUuid, '负责人');
      const person = item(s, owner, 'teacher');
      if (person.status !== 'active' || person.campus !== row.campus)
        throw new BusinessError('VALIDATION', '请选择来源校区的在职负责人。');
      if (
        !input.dueAt ||
        !Number.isFinite(Date.parse(input.dueAt)) ||
        Date.parse(input.dueAt) <= Date.parse(s.now)
      )
        throw new BusinessError('VALIDATION', '期限必须晚于当前业务时间。');
      const existing = s.entities.find(
        (e) =>
          e.kind === 'task' &&
          e.sourceUuid === row.uuid &&
          !['completed', 'closed', 'cancelled'].includes(e.status),
      );
      result =
        existing ||
        append(s, 'task', `${advice.action}：${row.title}`, {
          status: 'pending',
          sourceUuid: row.uuid,
          campus: row.campus,
          memberUuid: row.memberUuid,
          owner: person.memberUuid,
          dueAt: input.dueAt,
          note: advice.reason,
          fields: {
            recommendation: advice.action,
            sourceRevision: String(row.revision),
            confirmedBy: identity.memberUuid,
          },
        });
      if (!existing) notify(s, result, `工作台行动已安排：${advice.action}`);
      break;
    }
    case 'deviceReport': {
      if (!row || row.kind !== 'device' || !row.supportsRemote)
        throw new BusinessError(
          'CAPABILITY_UNAVAILABLE',
          '当前型号不支持远程上报。',
        );
      row.reportedAt = s.now;
      row.battery = 90;
      break;
    }
    case 'exportMetadata': {
      let ids: unknown;
      try {
        ids = JSON.parse(input.fields?.ids || '[]');
      } catch {
        throw new BusinessError('VALIDATION', '导出对象无效');
      }
      if (
        !Array.isArray(ids) ||
        ids.length < 1 ||
        ids.length > 500 ||
        ids.some((id) => typeof id !== 'string')
      )
        throw new BusinessError('VALIDATION', '请选择1–500条记录');
      for (const id of ids) item(s, id, input.kind);
      result = item(s, ids[0], input.kind);
      break;
    }
    case 'dataPolicy': {
      const target = s.entities.find(
        (e) => e.uuid === input.fields?.targetUuid && e.kind === 'recording',
      );
      if (
        !target ||
        (s.context.campus !== '全部校区' && target.campus !== s.context.campus)
      )
        throw new BusinessError('FORBIDDEN', '目标不在管理范围');
      if (
        input.fields?.targetRevision &&
        String(target.revision) !== input.fields.targetRevision
      )
        throw new BusinessError('CONFLICT', '资料版本已变化，请重新预览');
      const op = input.fields?.operation;
      if (!['lock', 'unlock', 'archive', 'restore'].includes(op || ''))
        throw new BusinessError('VALIDATION', '策略动作无效');
      if (
        op === 'archive' &&
        (target.fields?.retentionLock === 'true' ||
          recordingReferences(s, target.uuid) > 0)
      )
        throw new BusinessError(
          'VALIDATION',
          '录音已保留锁定或被证据引用，不能删除',
        );
      target.fields = {
        ...target.fields,
        ...(op === 'lock' || op === 'unlock'
          ? { retentionLock: String(op === 'lock') }
          : { softDeleted: String(op === 'archive') }),
      };
      changed(target, s);
      result = target;
      break;
    }
    case 'replaceDevice': {
      if (!row || row.kind !== 'repair')
        throw new BusinessError('VALIDATION', '请选择维修记录');
      const oldDevice = item(s, row.fields?.deviceUuid, 'device');
      const replacement = item(s, input.fields?.replacementUuid, 'device');
      if (row.fields?.replacementUuid)
        throw new BusinessError('CONFLICT', '此维修记录已完成换机');
      if (row.fields?.previousLifecycle !== 'assigned')
        throw new BusinessError(
          'VALIDATION',
          '原设备故障时未领用，无需为人员换机',
        );
      requireState(replacement, ['stock']);
      if (oldDevice.campus !== replacement.campus)
        throw new BusinessError('VALIDATION', '请选择同校区备用设备');
      replacement.status = 'assigned';
      replacement.memberUuid =
        row.fields?.previousMemberUuid || oldDevice.memberUuid;
      append(s, 'binding', `${replacement.title}故障替换领用`, {
        deviceUuid: replacement.uuid,
        memberUuid: replacement.memberUuid,
        campus: replacement.campus,
        startAt: s.now,
        status: 'active',
        note: `替换${oldDevice.title}`,
      });
      changed(replacement, s);
      row.fields = { ...row.fields, replacementUuid: replacement.uuid };
      break;
    }
    case 'locationStep': {
      if (!row || row.kind !== 'device' || !row.supportsRemote)
        throw new BusinessError(
          'CAPABILITY_UNAVAILABLE',
          '型号不支持或位置能力待验证',
        );
      const previous = s.entities
        .filter((e) => e.kind === 'position' && e.deviceUuid === row?.uuid)
        .at(-1);
      result = append(s, 'position', `${row.title}模拟位置`, {
        deviceUuid: row.uuid,
        campus: row.campus,
        status: 'ready',
        fields: {
          x: String((Number(previous?.fields?.x || 0.1) + 0.15) % 1),
          y: String((Number(previous?.fields?.y || 0.2) + 0.1) % 1),
          accuracy: '30',
          method: '模拟WiFi定位',
        },
      });
      if (
        previous &&
        Date.parse(s.now) - Date.parse(previous.createdAt) <= 15 * 60000 &&
        Number(previous.fields?.accuracy) <= 50
      ) {
        const events: string[] = [];
        for (const zone of s.entities.filter(
          (e) =>
            e.kind === 'zone' &&
            e.status === 'published' &&
            e.campus === row.campus,
        )) {
          let polygon: number[][] = [];
          try {
            polygon = JSON.parse(zone.fields?.points || '[]');
          } catch {}
          if (polygon.length < 3) continue;
          const was = insidePolygon(
              Number(previous.fields?.x),
              Number(previous.fields?.y),
              polygon,
            ),
            now = insidePolygon(
              Number(result.fields?.x),
              Number(result.fields?.y),
              polygon,
            );
          if (was !== now)
            events.push(
              `${zone.title}（版本${zone.revision}）：${now ? '进入' : '离开'}，需人工核实`,
            );
        }
        if (events.length) {
          result.fields = { ...result.fields, zoneEvents: events.join('；') };
          notify(s, result, events.join('；'));
        }
      } else result.note = '位置存在时间缺口，不能推断连续轨迹或进出事件';
      break;
    }
    case 'locationConfirm': {
      if (!row || row.kind !== 'position')
        throw new BusinessError('VALIDATION', '请选择位置记录');
      row.note = required(input.note, '人工说明');
      row.status = 'confirmed';
      break;
    }
    case 'importAudio': {
      const durationMs = Number(input.fields?.durationMs);
      if (
        !input.audioBlob ||
        input.audioBlob.size > 50 * 1024 * 1024 ||
        !Number.isFinite(durationMs) ||
        durationMs <= 0
      )
        throw new BusinessError('VALIDATION', '音频校验未通过');
      const existing = s.entities.find(
        (e) => e.kind === 'recording' && e.hash === input.note,
      );
      result =
        existing ||
        append(s, 'recording', required(input.title, '标题'), {
          status: 'ready',
          hash: input.note,
          audioBlob: input.audioBlob,
          startAt: s.now,
          fields: { ...input.fields, imported: 'true', linkStatus: 'pending' },
          note: '模拟转写，非文件实际识别结果',
          versions: [
            {
              uuid: crypto.randomUUID(),
              createdAt: s.now,
              segments: [
                {
                  uuid: crypto.randomUUID(),
                  startMs: 0,
                  endMs: durationMs,
                  speaker: '说话人A（未确认）',
                  text: '本录音尚未执行真实转写。当前为模拟占位片段，不代表实际音频内容。',
                },
              ],
            },
          ],
        });
      break;
    }
    case 'confirmLink': {
      if (!row || row.kind !== 'recording')
        throw new BusinessError('VALIDATION', '请选择录音');
      const lesson = item(s, input.fields?.lessonUuid, 'lesson');
      row.fields = {
        ...row.fields,
        linkStatus: 'confirmed',
        lessonUuid: lesson.uuid,
      };
      break;
    }
    case 'bind': {
      if (!row || row.kind !== 'device')
        throw new BusinessError('VALIDATION', '请选择设备。');
      requireState(row, ['stock']);
      const startAt = input.startAt || s.now;
      if (!Number.isFinite(Date.parse(startAt)))
        throw new BusinessError('VALIDATION', '生效时间无效。');
      const memberUuid = required(input.memberUuid, '使用人');
      if (
        s.entities.some(
          (e) =>
            e.kind === 'binding' &&
            e.deviceUuid === row?.uuid &&
            (!e.endAt || Date.parse(e.endAt) > Date.parse(startAt)),
        )
      )
        throw new BusinessError('CONFLICT', '设备已有重叠绑定，请先归还。');
      row.status = 'assigned';
      row.memberUuid = memberUuid;
      append(s, 'binding', `${row.title}领用`, {
        deviceUuid: row.uuid,
        memberUuid,
        campus: row.campus,
        startAt,
        status: 'active',
      });
      break;
    }
    case 'return': {
      if (!row || row.kind !== 'device')
        throw new BusinessError('VALIDATION', '请选择设备。');
      requireState(row, ['assigned']);
      const binding = s.entities.find(
        (e) => e.kind === 'binding' && e.deviceUuid === row?.uuid && !e.endAt,
      );
      if (binding) {
        binding.endAt = s.now;
        binding.status = 'completed';
      }
      row.status = 'stock';
      break;
    }
    case 'collect': {
      if (!row || row.kind !== 'device')
        throw new BusinessError('VALIDATION', '请选择设备。');
      requireState(row, ['assigned']);
      if (
        s.entities.some(
          (e) =>
            e.kind === 'batch' &&
            e.deviceUuid === row?.uuid &&
            !['completed', 'failed', 'cancelled'].includes(e.status),
        )
      )
        throw new BusinessError('CONFLICT', '该设备已有进行中的采集。');
      const station = s.entities.find(
        (e) =>
          e.kind === 'station' &&
          e.campus === row.campus &&
          e.status === 'active',
      );
      if (!station)
        throw new BusinessError(
          'CAPABILITY_UNAVAILABLE',
          '该校区暂无启用的采集站，请先启用站点',
        );
      result = append(s, 'batch', `${row.title}模拟采集`, {
        deviceUuid: row.uuid,
        memberUuid: row.memberUuid,
        campus: row.campus,
        status: 'pending',
        progress: 0,
        failOnce: input.note === 'fail',
        startAt: s.now,
        fields: { lastTick: s.now, stationUuid: station.uuid },
      });
      break;
    }
    case 'queue': {
      if (!row || row.kind !== 'batch')
        throw new BusinessError('VALIDATION', '请选择批次。');
      const command = required(input.note, '队列动作');
      if (command === 'pause') {
        requireState(row, [
          'pending',
          'connecting',
          'transferring',
          'confirming',
        ]);
        row.stage = row.status;
        row.status = 'paused';
      } else if (command === 'cancel') {
        requireState(row, [
          'pending',
          'connecting',
          'transferring',
          'confirming',
          'paused',
        ]);
        row.status = 'cancelled';
        row.generation = (row.generation || 0) + 1;
      } else if (command === 'retry') {
        requireState(row, ['failed']);
        row.status = 'pending';
      } else if (command === 'resume') {
        requireState(row, ['paused']);
        row.status = row.stage || 'pending';
      } else throw new BusinessError('VALIDATION', '不支持的队列动作。');
      break;
    }
    case 'jobControl': {
      if (!row || row.kind !== 'job')
        throw new BusinessError('VALIDATION', '请选择分析任务');
      if (input.note === 'cancel') {
        requireState(row, ['queued', 'running']);
        row.status = 'cancelled';
        row.generation = (row.generation || 0) + 1;
      } else if (input.note === 'retry') {
        requireState(row, ['failed']);
        row.status = 'queued';
        row.progress = 0;
        row.fields = { ...row.fields, startedAt: '' };
      } else throw new BusinessError('VALIDATION', '任务控制动作无效');
      break;
    }
    case 'analyze': {
      const subscription = s.entities.find(
        (e) => e.kind === 'subscription' && e.status === 'published',
      );
      if (
        subscription &&
        (Date.parse(subscription.fields?.expiresAt || '') < Date.parse(s.now) ||
          s.entities.filter(
            (e) =>
              e.kind === 'job' &&
              e.status !== 'cancelled' &&
              e.status !== 'failed',
          ).length >= Number(subscription.fields?.analysisLimit))
      )
        throw new BusinessError(
          'QUOTA_EXCEEDED',
          '模拟服务已到期或分析任务配额已用尽',
        );
      if (!row || row.kind !== 'recording' || !row.versions?.length)
        throw new BusinessError('VALIDATION', '录音尚无可分析转写。');
      result = append(s, 'job', `${row.title} · 模拟分析`, {
        sourceUuid: row.uuid,
        campus: row.campus,
        memberUuid: row.memberUuid,
        status: 'queued',
        progress: 0,
        fields: {
          transcriptVersionUuid: row.versions.at(-1)?.uuid || '',
          lastTick: s.now,
          templateVersion: '1',
          ruleVersion: '1',
          unitPrice: subscription?.fields?.unitPrice || '1',
          timeoutMs:
            s.entities.find(
              (e) => e.kind === 'modelConfig' && e.status === 'published',
            )?.fields?.timeoutMs || '10000',
          ruleSnapshot: JSON.stringify(
            s.entities
              .filter((e) => e.kind === 'rule' && e.status === 'published')
              .map((e) => ({
                uuid: e.uuid,
                revision: e.revision,
                title: e.title,
                fields: e.fields,
              })),
          ),
        },
      });
      break;
    }
    case 'revise': {
      if (!row || row.kind !== 'recording')
        throw new BusinessError('VALIDATION', '请选择录音。');
      const text = required(input.note, '修订文本');
      const old = row.versions?.at(-1);
      if (!old) throw new BusinessError('VALIDATION', '无可修订转写。');
      if (
        input.fields?.previewAt &&
        (text.split('\n').length !== old.segments.length ||
          text.split('\n').some((line) => !line.trim()))
      )
        throw new BusinessError(
          'VALIDATION',
          '每行对应一个原片段，片段数量必须一致且不得为空。',
        );
      const segments = old.segments.map((seg, i) => ({
        ...seg,
        uuid: crypto.randomUUID(),
        text: text.split('\n')[i] || seg.text,
      }));
      row.versions?.push({
        uuid: crypto.randomUUID(),
        createdAt: s.now,
        segments,
      });
      for (const related of s.entities.filter(
        (e) => e.evidence?.recordingUuid === row?.uuid,
      ))
        related.fields = {
          ...related.fields,
          sourceChanged: 'true',
          sourceChangeNote: '来源已有新版本，请核实',
        };
      break;
    }
    case 'mark': {
      if (!row || row.kind !== 'recording')
        throw new BusinessError('VALIDATION', '请选择录音。');
      row.fields = {
        ...row.fields,
        bookmarked: row.fields?.bookmarked === 'true' ? 'false' : 'true',
      };
      break;
    }
    case 'clue': {
      const evidence = checkEvidence(s, input.evidence);
      const recording = item(s, evidence.recordingUuid);
      const existing = s.entities.find(
        (e) =>
          e.kind === 'clue' &&
          e.evidence?.recordingUuid === evidence.recordingUuid &&
          e.evidence?.transcriptVersionUuid ===
            evidence.transcriptVersionUuid &&
          e.evidence?.startMs === evidence.startMs &&
          e.evidence?.endMs === evidence.endMs,
      );
      result =
        existing ||
        append(s, 'clue', input.title?.trim() || '待复核沟通片段', {
          evidence,
          status: 'unassigned',
          campus: recording.campus,
          memberUuid: recording.memberUuid,
          note: input.note || '',
        });
      break;
    }
    case 'assign': {
      if (
        !row ||
        !['clue', 'case', 'task', 'ticket', 'repair'].includes(row.kind)
      )
        throw new BusinessError('VALIDATION', '对象不能分派。');
      row.owner = required(input.memberUuid, '负责人');
      if (row.kind === 'clue') row.status = 'pendingReview';
      if (row.kind === 'case') row.status = 'inProgress';
      row.dueAt = input.dueAt || row.dueAt;
      makeTask(s, row, `处理：${row.title}`);
      break;
    }
    case 'review': {
      if (!row || row.kind !== 'clue')
        throw new BusinessError('VALIDATION', '请选择线索。');
      requireState(row, ['pendingReview', 'needsInfo']);
      const note = required(input.note, '复核意见');
      const conclusion = required(input.conclusion, '复核结论');
      if (!['成立', '部分成立', '不成立', '信息不足'].includes(conclusion))
        throw new BusinessError('VALIDATION', '请选择有效结论。');
      row.status =
        conclusion === '信息不足'
          ? 'needsInfo'
          : conclusion === '不成立'
            ? 'excluded'
            : 'confirmed';
      row.conclusion = conclusion;
      row.note = note;
      for (const task of s.entities.filter(
        (e) => e.kind === 'task' && e.sourceUuid === row?.uuid,
      )) {
        task.status = 'completed';
        changed(task, s);
      }
      row.fields = {
        ...row.fields,
        [`review-${row.revision}`]: JSON.stringify({
          conclusion,
          note,
          reviewer: s.context.viewMemberUuid,
          at: s.now,
        }),
      };
      notify(s, row, `复核结果：${row.title}`);
      break;
    }
    case 'case': {
      if (!row || row.kind !== 'clue')
        throw new BusinessError('VALIDATION', '请选择已确认线索。');
      requireState(row, ['confirmed']);
      const existing = s.entities.find(
        (e) => e.kind === 'case' && e.sourceUuid === row?.uuid,
      );
      const dueAt = required(input.dueAt, '截止时间');
      if (Date.parse(dueAt) <= Date.parse(s.now))
        throw new BusinessError('VALIDATION', '截止时间须晚于当前演示时间。');
      result =
        existing ||
        append(s, 'case', input.title?.trim() || row.title, {
          sourceUuid: row.uuid,
          campus: row.campus,
          memberUuid: row.memberUuid,
          evidence: row.evidence,
          status: 'inProgress',
          owner: required(input.memberUuid, '负责人'),
          dueAt,
        });
      if (!existing) makeTask(s, result, `处理事项：${result.title}`);
      break;
    }
    case 'task': {
      if (!row || row.kind !== 'task')
        throw new BusinessError('VALIDATION', '请选择任务。');
      requireState(row, ['pending', 'inProgress']);
      row.note = required(input.note, '完成说明');
      row.status = 'completed';
      break;
    }
    case 'submit': {
      if (!row || row.kind !== 'case')
        throw new BusinessError('VALIDATION', '请选择事项。');
      requireState(row, ['inProgress', 'reopened']);
      row.result = required(input.note, '处理结果');
      row.status = 'pendingAcceptance';
      break;
    }
    case 'close': {
      if (!row || row.kind !== 'case')
        throw new BusinessError('VALIDATION', '请选择事项。');
      requireState(row, ['pendingAcceptance']);
      required(row.result, '处理结果');
      required(row.owner, '负责人');
      if (
        s.entities.some(
          (e) =>
            e.kind === 'task' &&
            e.sourceUuid === row?.uuid &&
            e.status !== 'completed',
        )
      )
        throw new BusinessError('VALIDATION', '请先完成事项关联任务。');
      row.note = required(input.note, '验收意见');
      row.status = 'closed';
      row.endAt = s.now;
      const clue = s.entities.find((e) => e.uuid === row?.sourceUuid);
      if (clue) {
        clue.status = 'closed';
        changed(clue, s);
      }
      notify(s, row, `事项已关闭：${row.title}`);
      break;
    }
    case 'reopen': {
      if (!row || row.kind !== 'case')
        throw new BusinessError('VALIDATION', '请选择事项。');
      requireState(row, ['closed']);
      row.note = required(input.note, '重开原因');
      row.status = 'reopened';
      makeTask(s, row, `重新处理：${row.title}`);
      break;
    }
    case 'read': {
      if (!row || row.kind !== 'message')
        throw new BusinessError('VALIDATION', '请选择消息。');
      row.status = 'read';
      break;
    }
    default:
      result = moduleCommand(s, input, {
        append,
        get: item,
        touch: changed,
        task: makeTask,
      });
  }
  if (row && input.action !== 'workspaceTask') changed(row, s);
  const uuid = result?.uuid || '';
  append(
    s,
    'audit',
    input.action === 'workspaceTask' ? '已安排行动' : input.action,
    {
      sourceUuid: uuid,
      status: 'completed',
      note: input.note || '',
      fields: {
        actorMemberUuid: identity.memberUuid,
        viewMemberUuid: s.context.viewMemberUuid,
        role: s.context.role,
        realOccurredAt: new Date().toISOString(),
        fieldChanges: auditDifference(beforeAudit, result),
      },
    },
  );
  s.receipts[commandUuid] = uuid;
  s.revision++;
  return { state: s, uuid };
}

export function advance(
  s: Snapshot,
  milliseconds = 1000,
  identity?: SessionIdentity,
): Snapshot {
  s.now = new Date(Date.parse(s.now) + milliseconds).toISOString();
  // A single active device transfer; a failed/paused item never blocks the next device.
  const batch = s.entities.find(
    (e) =>
      e.kind === 'batch' &&
      ['pending', 'connecting', 'transferring', 'confirming'].includes(
        e.status,
      ),
  );
  if (batch) {
    if (batch.status === 'pending') batch.status = 'connecting';
    else if (batch.status === 'connecting') batch.status = 'transferring';
    else if (batch.status === 'transferring') {
      if (batch.failOnce && !batch.failedBefore) {
        batch.status = 'failed';
        batch.failedBefore = true;
        batch.note = '模拟连接中断，可重试；后续设备继续。';
      } else {
        batch.progress = Math.min(100, (batch.progress || 0) + 25);
        if (batch.progress === 100) batch.status = 'confirming';
      }
    } else {
      batch.status = 'completed';
      batch.received = true;
      const binding = s.entities.find(
        (e) =>
          e.kind === 'binding' &&
          e.deviceUuid === batch.deviceUuid &&
          e.startAt &&
          Date.parse(e.startAt) <= Date.parse(batch.startAt || s.now) &&
          (!e.endAt ||
            Date.parse(e.endAt) > Date.parse(batch.startAt || s.now)),
      );
      const source = s.entities.find(
        (e) => e.kind === 'recording' && e.deviceUuid === batch.deviceUuid,
      );
      // Stable source-file identity means retry/repeated collection does not create another recording.
      const hash = `${batch.deviceUuid}:demo-source-1`;
      if (!s.entities.some((e) => e.kind === 'recording' && e.hash === hash)) {
        const seedRecording = s.entities.find(
          (e) => e.kind === 'recording' && e.versions?.length,
        );
        const recording = append(
          s,
          'recording',
          `${batch.title.replace('模拟采集', '')} · 新归集录音`,
          {
            status: 'ready',
            deviceUuid: batch.deviceUuid,
            memberUuid: binding?.memberUuid || batch.memberUuid,
            campus: binding?.campus || batch.campus,
            fixture: source?.fixture || 0,
            note: '本地生成的虚构录音；预置演示转写，不是实际设备或识别接入。',
            versions: structuredClone(
              ((source || seedRecording)?.versions || []).map((version) => ({
                ...version,
                uuid: crypto.randomUUID(),
                segments: version.segments.map((segment) => ({
                  ...segment,
                  uuid: crypto.randomUUID(),
                })),
              })),
            ),
            startAt: batch.startAt,
            sourceUuid: batch.uuid,
            hash,
          },
        );
        notify(s, recording, '新录音已归集，请查看并确认内容。');
      } else batch.note = '重复文件已确认，未重复生成录音。';
    }
    changed(batch, s);
  }
  for (const job of s.entities
    .filter(
      (e) =>
        e.kind === 'job' &&
        !e.fields?.analysisTask &&
        ['queued', 'running'].includes(e.status),
    )
    .slice(
      0,
      Number(
        s.entities.find(
          (e) => e.kind === 'modelConfig' && e.status === 'published',
        )?.fields?.parallel || 2,
      ),
    )) {
    if (job.status === 'queued') {
      job.status = 'running';
      job.fields = { ...job.fields, startedAt: s.now };
    } else {
      if (
        job.fields?.startedAt &&
        Date.parse(s.now) - Date.parse(job.fields.startedAt) >
          Number(job.fields?.timeoutMs || 10000)
      ) {
        job.status = 'failed';
        job.note = '模拟分析超时，可重试或调整后重新分析';
        changed(job, s);
        continue;
      }
      job.progress = Math.min(100, (job.progress || 0) + 25);
      if (job.progress === 100) {
        job.status = 'succeeded';
        const recording = s.entities.find((e) => e.uuid === job.sourceUuid);
        const text =
          recording?.versions
            ?.find((v) => v.uuid === job.fields?.transcriptVersionUuid)
            ?.segments.map((seg) => seg.text)
            .join(' ') || '';
        const rules: Pick<Entity, 'uuid' | 'revision' | 'title' | 'fields'>[] =
          JSON.parse(job.fields?.ruleSnapshot || '[]');
        const matches = rules
          .filter(
            (rule) =>
              rule.fields?.keyword &&
              text.includes(rule.fields.keyword) &&
              (!rule.fields.exclusion || !text.includes(rule.fields.exclusion)),
          )
          .map((e) => e.title);
        job.result = `模拟关键词与模板分析：${matches.length ? `关注 ${matches.join('、')}` : '未命中已发布规则'}。建议人工核实承诺与后续安排，候选不代表正式结论。`;
        job.fields = {
          ...job.fields,
          ruleSnapshot: JSON.stringify(
            rules.map((e) => ({
              uuid: e.uuid,
              revision: e.revision,
              fields: e.fields,
            })),
          ),
        };
        notify(s, job, '模拟分析已完成。');
      }
    }
    changed(job, s);
  }
  for (const domain of identity ? registeredDomains() : [])
    domain.tick?.(
      domainContext(
        s,
        identity || { appId: '', tenantUuid: '', memberUuid: '' },
        domain.namespace,
        crypto.randomUUID(),
      ),
      milliseconds,
    );
  s.revision++;
  return s;
}
export function domainContext(
  s: Snapshot,
  identity: SessionIdentity,
  namespace: string,
  commandUuid: string,
): DomainCommandContext {
  const get = (uuid: string | undefined) =>
    s.entities.find((e) => e.uuid === uuid && canAccessEntity(s, e, canRead));
  const project = (row: Entity): Entity => {
    if (
      namespace !== 'knowledge' &&
      row.kind === 'knowledge' &&
      row.status === 'published' &&
      row.evidence &&
      !get(row.evidence.recordingUuid)
    ) {
      const { evidence: _, ...rest } = row;
      return rest;
    }
    return row;
  };
  return {
    identity,
    context: s.context,
    now: s.now,
    revision: s.revision,
    commandUuid,
    get: (uuid) => {
      const config = s.entities.find((e) => e.uuid === uuid);
      if (config && isExecutionProjection(namespace, config.kind))
        return publishedExecutionSettings(s, config.kind).find(
          (e) => e.uuid === uuid,
        );
      const row = get(uuid);
      return row ? project(row) : undefined;
    },
    list: (kind) =>
      isExecutionProjection(namespace, kind)
        ? publishedExecutionSettings(s, kind)
        : s.entities
            .filter((e) => e.kind === kind && canAccessEntity(s, e, canRead))
            .map(project),
    members: () =>
      s.entities
        .filter(
          (e) =>
            e.kind === 'teacher' &&
            canRead(e, s.context) &&
            e.status === 'active',
        )
        .map(({ uuid, memberUuid, title, campus, status }) => ({
          uuid,
          memberUuid,
          title,
          campus,
          status,
        })),
    data: () => {
      const stored = s.domainData?.[namespace] || {};
      if (!['operations', 'pilot'].includes(namespace)) return stored;
      const collectionReceipts = (
        canOpenResource('collection', s) && policyAllows(s, 'batch', 'read')
          ? collectionData(s)?.files || []
          : []
      )
        .filter(
          (f) =>
            f.confirmedAt &&
            f.integrity === 'valid' &&
            !f.duplicateOfFileUuid &&
            f.recordingUuid &&
            get(f.recordingUuid),
        )
        .map((f) => ({
          uuid: f.uuid,
          recordingUuid: f.recordingUuid || '',
          deviceUuid: f.deviceUuid || '',
          confirmedAt: f.confirmedAt || '',
          campus: f.association.campus,
          sizeBytes: f.sizeBytes,
        }));
      return { ...stored, collectionReceipts };
    },
    setData: (value) => {
      s.domainData ||= {};
      s.domainData[namespace] = value;
    },
    append: (kind, title, extra) => append(s, kind, title, extra),
    touch: (row) => changed(row, s),
    notify: (row, title) => notify(s, row, title),
    task: (row, title) => makeTask(s, row, title),
  };
}
export class BusinessService {
  state: Snapshot | undefined;
  private listeners = new Set<() => void>();
  constructor(
    public repository: Repository,
    public identity: SessionIdentity,
  ) {}
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  snapshot = () => this.state;
  publish(state: Snapshot) {
    this.state = state;
    this.listeners.forEach((fn) => {
      fn();
    });
  }
  async load() {
    const state = await this.repository.change((current) => {
      const next = structuredClone(current || createSeed());
      const versions = JSON.stringify(next.domainVersions || {});
      migrateDomains(next);
      if (versions !== JSON.stringify(next.domainVersions)) next.revision++;
      return next;
    });
    this.publish(state);
  }
  async refresh() {
    const s = await this.repository.read();
    if (s && s.revision !== this.state?.revision) this.publish(s);
  }
  async command(input: Command) {
    let uuid = '';
    const s = await this.repository.change((current) => {
      if (!current) throw new BusinessError('NOT_READY', '数据尚未初始化。');
      const result = reduceCommand(
        structuredClone(current),
        input,
        this.identity,
      );
      uuid = result.uuid;
      return result.state;
    });
    this.publish(s);
    return uuid;
  }
  async context(context: Context) {
    const s = await this.repository.change((current) => {
      const next = structuredClone(current || createSeed());
      next.context = context;
      next.revision++;
      return next;
    });
    this.publish(s);
  }
  async draft(key: string, value: string) {
    const s = await this.repository.change((current) => {
      const next = structuredClone(current || createSeed());
      next.drafts[key] = value;
      next.revision++;
      return next;
    });
    this.publish(s);
  }
  async tick(force = false) {
    const s = await this.repository.change((current) => {
      if (!current) return createSeed();
      const last = Number(current.drafts.__lastTick || 0);
      if (!force && Date.now() - last < 950) return current;
      let next = advance(structuredClone(current), 1000, this.identity);
      if (hasCollectionWork(next))
        next = reduceCollection(
          next,
          { type: 'tick', at: Date.now() },
          { commandUuid: crypto.randomUUID() },
          this.identity,
        ).state;
      next.drafts.__lastTick = String(Date.now());
      return next;
    });
    if (s.revision !== this.state?.revision) this.publish(s);
  }
  async reset() {
    const s = await this.repository.change((current) => {
      const next = createSeed();
      next.revision = (current?.revision || 0) + 1;
      return next;
    });
    this.publish(s);
  }
  hasScheduledWork() {
    const s = this.state;
    return (
      !!s &&
      (hasCollectionWork(s) ||
        s.entities.some(
          (e) =>
            ['batch', 'job'].includes(e.kind) &&
            [
              'pending',
              'connecting',
              'transferring',
              'confirming',
              'queued',
              'running',
            ].includes(e.status),
        ) ||
        registeredDomains().some((d) =>
          d.isActive?.(this.readContext(d.namespace)),
        ))
    );
  }
  members() {
    const s = this.state;
    return s
      ? s.entities
          .filter(
            (e) =>
              e.kind === 'teacher' &&
              canRead(e, s.context) &&
              e.status === 'active',
          )
          .map(({ uuid, memberUuid, title, campus, status }) => ({
            uuid,
            memberUuid,
            title,
            campus,
            status,
          }))
      : [];
  }
  readContext(namespace = 'work'): DomainReadContext {
    if (!this.state) throw new BusinessError('NOT_READY', '业务数据尚未加载');
    return domainContext(
      structuredClone(this.state),
      this.identity,
      namespace,
      'query',
    );
  }
  query(namespace: string, input: QueryInput = {}) {
    if (!this.state) throw new BusinessError('NOT_READY', '业务数据尚未加载');
    const module = getDomain(namespace);
    if (!module?.query)
      throw new BusinessError('UNAVAILABLE', '领域查询尚未注册');
    if (!canOpenResource(module.resource, this.state))
      throw new BusinessError('FORBIDDEN', '无权查询此领域');
    return module.query(this.readContext(namespace), input);
  }
  search(input: QueryInput = {}): SearchHit[] {
    if (!this.state) return [];
    const terms = expandTerms(input.keyword || '');
    if (!terms.length) return [];
    const seen = new Set<string>();
    return registeredDomains()
      .filter((m) => canOpenResource(m.resource, this.state as Snapshot))
      .flatMap((m) =>
        (m.search?.(this.readContext(m.namespace)) || []).filter((doc) => {
          const row = this.get(doc.entityUuid);
          return (
            row?.kind !== 'knowledge' ||
            row.fields?.learningManaged !== 'true' ||
            m.namespace === 'knowledge'
          );
        }),
      )
      .filter((doc) => {
        if (
          seen.has(doc.id) ||
          !this.get(doc.entityUuid) ||
          (doc.evidence && !this.get(doc.evidence.recordingUuid))
        )
          return false;
        seen.add(doc.id);
        if (input.kinds?.length && !input.kinds.includes(doc.kind))
          return false;
        if (input.memberUuid && doc.memberUuid !== input.memberUuid)
          return false;
        const at = Date.parse(doc.occurredAt);
        if (input.from && at < Date.parse(input.from)) return false;
        if (input.to && at > Date.parse(input.to)) return false;
        return terms.some((t) =>
          doc.text.toLowerCase().includes(t.toLowerCase()),
        );
      })
      .map(({ text, ...doc }) => {
        const matchedTerms = terms.filter((t) =>
          text.toLowerCase().includes(t.toLowerCase()),
        );
        const i = Math.max(
          0,
          text.toLowerCase().indexOf(matchedTerms[0]?.toLowerCase() || ''),
        );
        return {
          ...doc,
          excerpt: text.slice(Math.max(0, i - 35), i + 160),
          matchedTerms,
          reason: matchedTerms.some((t) => t === (input.keyword || '').trim())
            ? '关键词命中'
            : '预置同义词命中',
        };
      })
      .sort((a, b) => {
        const rank = (hit: SearchHit) =>
          hit.matchedTerms.reduce(
            (sum, t) =>
              sum +
              (t === (input.keyword || '').trim()
                ? 100
                : ['本周五', '答应'].includes(t)
                  ? 30
                  : 1),
            0,
          );
        return (
          rank(b) - rank(a) ||
          Date.parse(b.occurredAt) - Date.parse(a.occurredAt) ||
          a.id.localeCompare(b.id)
        );
      });
  }
  suggestions() {
    return registeredDomains()
      .filter((m) => this.state && canOpenResource(m.resource, this.state))
      .flatMap((m) => m.suggestions?.(this.readContext(m.namespace)) || [])
      .filter((s) => !!this.get(s.sourceUuid));
  }
  canExecute(action: string, uuid?: string) {
    if (!this.state || (uuid && !this.get(uuid))) return false;
    const resolved = resolveDomainCommand(action);
    return resolved
      ? canOpenResource(resolved.module.resource, this.state) &&
          resolved.command.roles.includes(this.state.context.role) &&
          (!resolved.command.kind ||
            policyAllows(this.state, resolved.command.kind, 'write'))
      : !action.includes('.') && allowed(action, this.state.context);
  }
  preview(recommendation: ActionRecommendation): ActionPreview {
    const source = this.get(recommendation.sourceUuid);
    if (!source || !this.state)
      throw new BusinessError('FORBIDDEN', '来源不可访问');
    const now = Date.now();
    return {
      uuid: crypto.randomUUID(),
      createdAt: new Date(now).toISOString(),
      expiresAt: new Date(now + 900000).toISOString(),
      contextKey: contextKey(this.state.context),
      snapshotRevision: this.state.revision,
      sourceVersions: { [source.uuid]: source.revision },
      recommendation,
      impact: '确认后更新所示业务状态，并同步相关待办、消息与操作记录。',
    };
  }
  async confirm(preview: ActionPreview, input: Partial<Command> = {}) {
    if (
      !this.state ||
      contextKey(this.state.context) !== preview.contextKey ||
      Date.now() > Date.parse(preview.expiresAt)
    )
      throw new BusinessError(
        'CONFLICT',
        '预览已过期或业务视角已变化，请重新预览',
      );
    const source = this.get(preview.recommendation.sourceUuid);
    if (!source || source.revision !== preview.sourceVersions[source.uuid])
      throw new BusinessError('CONFLICT', '来源版本或权限已变化，请重新预览');
    return this.command({
      ...input,
      action: preview.recommendation.action,
      uuid: source.uuid,
      fields: {
        ...preview.recommendation.fields,
        ...input.fields,
        previewAt: String(Date.parse(preview.createdAt)),
      },
      meta: { commandUuid: preview.uuid, expectedRevision: source.revision },
    });
  }
  previewDeviceCommand(input: Command): { ok: boolean; reason: string } {
    if (!this.state) return { ok: false, reason: '模拟数据未加载' };
    try {
      reduceCommand(structuredClone(this.state), input, this.identity);
      return { ok: true, reason: '当前预检通过，确认时复核' };
    } catch (error) {
      return {
        ok: false,
        reason: error instanceof Error ? error.message : '预检失败',
      };
    }
  }
  deviceRecordingMetadata(
    deviceUuid: string,
  ): Pick<Entity, 'uuid' | 'startAt' | 'campus' | 'memberUuid'>[] {
    if (
      !this.state ||
      !this.get(deviceUuid) ||
      !canOpenResource('device', this.state) ||
      !policyAllows(this.state, 'recording', 'read')
    )
      return [];
    return this.state.entities
      .filter(
        (e) =>
          e.kind === 'recording' &&
          e.deviceUuid === deviceUuid &&
          e.fields?.softDeleted !== 'true' &&
          (this.state?.context.campus === '全部校区' ||
            e.campus === this.state?.context.campus) &&
          (this.state?.context.role !== 'teacher' ||
            e.memberUuid === this.state?.context.viewMemberUuid),
      )
      .map((e) => ({
        uuid: e.uuid,
        startAt: e.startAt,
        campus: e.campus,
        memberUuid: e.memberUuid,
      }));
  }
  private projectRead(row: Entity): Entity {
    const value = structuredClone(row);
    if (
      value.kind === 'knowledge' &&
      value.status === 'published' &&
      value.evidence
    ) {
      const source = this.state?.entities.find(
        (e) => e.uuid === value.evidence?.recordingUuid,
      );
      if (
        !source ||
        !this.state ||
        !canAccessEntity(this.state, source, canRead)
      )
        delete value.evidence;
    }
    return value;
  }
  configurationSummary() {
    if (!this.state) throw new BusinessError('NOT_READY', '业务数据尚未加载');
    return configurationSummary(this.state);
  }
  dataCatalog() {
    if (!this.state) throw new BusinessError('NOT_READY', '业务数据尚未加载');
    return dataCatalog(this.state, canRead);
  }
  permissionPreview(context: Context, uuid: string) {
    if (!this.state) throw new BusinessError('NOT_READY', '业务数据尚未加载');
    return permissionPreview(this.state, canRead, context, uuid);
  }
  list(kind: Kind) {
    const state = this.state;
    return state
      ? state.entities
          .filter((e) => e.kind === kind && canAccessEntity(state, e, canRead))
          .map((e) => this.projectRead(e))
      : [];
  }
  get(uuid: string | undefined) {
    const state = this.state;
    const row = state?.entities.find(
      (e) => e.uuid === uuid && canAccessEntity(state, e, canRead),
    );
    return row ? this.projectRead(row) : undefined;
  }
}
